import { defineComponent, reactive, SetupContext, readonly, onMounted, ref, toRefs, watch} from '@vue/composition-api';
import CommonCategory from '@/components/common/common-category.component.vue';   // カテゴリ選択共通コンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import HttpStatus from 'http-status-codes';
import { OfficialDocumentDeleteType } from '@/shared/type';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';                                  // 定数定義

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                         // Windowサイズ
    officialDocument: OfficialDocumentDeleteType;                                 // 公文書フォーム
    numberPrefix: string;                                                         // 発番接頭辞
    organizationId: string;                                                       // カテゴリ選択で選択した組織ID
}

/**
 * 公文書削除画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor, CommonCategory },
    setup(props, ctx: SetupContext) {
        const issueDivision = {                                                     // 発番ラジオボタンの値
            notEntered: SystemConst.ISSUE_DIVISION.NOT_ENABLED,
            select: SystemConst.ISSUE_DIVISION.SELECT,
            officeContact: SystemConst.ISSUE_DIVISION.OFFICE_CONTACT
        };
        const categoryRef = ref<any>(null);                                         // カテゴリー選択

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                             // ウィンドウサイズ
            organizationId: '',                                                             // カテゴリ選択で選択した組織ID
            officialDocument: {
                organizationId: '',
                numberPrefix: '',
                issueDivision: SystemConst.ISSUE_DIVISION.NOT_ENABLED,
                issueNo: '',
                title: '',
                issueDate: '',
                files: [],
                body: ''
            },                                                                      // 公文書フォーム
            numberPrefix: '',                                                       // 発番接頭辞
        });
        const router = vueRouter;                                                   // ルータ
        const commonRef = ref<any>(null);                                           // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        let officialDocumentId = '';                                                // 削除対象の公文書ID
        const formHeight = SystemConst.FORM_HEIGHT.OFFICIAL_DOCUMENT;               // フォーム画面の高さ
        // カテゴリ選択のアクティブノードを監視する        
        const { activeNode } = toRefs(ctx.root.$store.state.officialDocument);
        // カテゴリが選択された場合
        watch(activeNode, (newVal, oldVal) => {
            // 初期表示でない場合
            if (oldVal.length !== 0) {
                // 公文書一覧（一般）画面へ遷移
                selectCategory();
            }
        })

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        })

        async function init() {
            // カテゴリ取得
            await categoryRef.value.getCategory();
            state.organizationId = ctx.root.$store.state.officialDocument.activeNode[0];

            // 公文書更新/削除対象取得APIで初期表示データを取得する
            // パラメータ:公文書ID
            const response = await ctx.root.$store.dispatch('officialDocument/getOperateDocument',
                {
                    officialDocumentId: officialDocumentId
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    /// HTTPステータスコードが404の場合、displayErrorMessageTransitionDialogメソッドで
                    // （管理向け）公文書一覧画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/official-documents/admin/list');
                } else {
                    // HTTPステータスコードが200, 404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面へ遷移して処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            // 取得したデータを画面へ設定する
            state.officialDocument = response.data.officialDocument;

            // 発出日のフォーマットをYYYY-MM-DDからYYYY/MM/DDに変換する
            state.officialDocument.issueDate = state.officialDocument.issueDate.replaceAll('-', '/');

            // 発番接頭辞の後ろに「第」を設定する
            state.numberPrefix = response.data.officialDocument.numberPrefix + " 第";
        }

        /**
         * 初期表示
         */
        created();
        async function created() {

            // 削除対象公文書のID取得
            officialDocumentId = ctx.root.$route.params.id;

            // titleIconを'mdi-text-box-remove-outline'に変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-remove-outline');

            // mainTitleを「公文書削除」に変更
            ctx.root.$store.commit('setMainTitle', '公文書削除');
        }

        /**
         * 削除ボタン押下時に確認メッセージダイアログ表示
         */
        function displayDialog() {
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['削除']);
            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 削除画面にて、確認メッセージダイアログの「はい」を押下時の処理
         */
        async function processingData() {
            // 公文書削除APIを呼び出す
            // パラメータ: 公文書ID
            const response = await ctx.root.$store.dispatch('officialDocument/deleteDocument',
                {
                    'officialDocumentId': officialDocumentId
                }
            );

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    //  HTTPステータスコードが404の場合
                    //  displayErrorMessageTransitionDialogメソッドで公文書一覧（管理）に遷移
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/official-documents/admin/list');
                } else {
                    //  HTTPステータスコードが404以外の場合
                    //  errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }
            // HTTPステータスコードが200の場合
            //  削除完了メッセージダイアログを表示して公文書一覧（管理）に遷移
            commonRef.value.displayMessageTransitionDialog(response, '/official-documents/admin/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            // storeのページの最終データフラグをfalseに設定
            ctx.root.$store.commit('officialDocument/setIsPageLastData', false);

            router.push('/official-documents/admin/list');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            // 現在のウィンドウの幅、高さを保存
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // 公文書一覧（一般）画面へ遷移
            router.push('/official-documents/list');
        }

        return {
            state: readonly(state),
            commonRef,
            issueDivision,
            categoryRef,
            formHeight,
            displayDialog,
            processingData,
            back,
            onResize
        };
    }
});

